/*
  ml-checkout-guard — the pre-checkout member price guard (MON-457).

  Immediately before checkout starts, it reads the cart as Shopify holds it
  (sections/ml-cart-guard.liquid through the Section Rendering API) and checks every
  product line:
    - member pricing is expected (a membership or trial is in the cart; tagged members are
      priced by Bold and are not enforced here),
    - the line has a real member price (variant.metafields.monthlees.member_price) below
      what it costs now,
    - and Shopify has not discounted it (the MON-456 race: the cart showed member prices,
      checkout would charge retail), or has discounted it to more than 5% above the member
      price.
  On a mismatch it repairs the cart by removing and re-adding those lines (same variant,
  quantity, selling plan and properties; re-adding is what fixed the one correctly priced
  line in the Christine Shepard session), then checks again. If the repair did not work,
  checkout is paused and a plain message says which products are affected and offers:
  try again, review the cart, or continue knowingly at the total checkout will charge.

  It never raises a price and never removes a product for good. It fails open: any error,
  or no answer within 5 seconds, lets checkout proceed as before. Every mismatch fires the
  PostHog event member_price_mismatch, whether or not the repair worked.

  Express wallet buttons (Shop Pay, Apple Pay) run in Shopify's iframes and cannot be
  intercepted; they go straight to Shopify's checkout.
*/
(function () {
  'use strict';

  if (window.MlCheckoutGuard) return;

  var SECTION = 'ml-cart-guard';
  var TIMEOUT_MS = 5000;
  var SETTLE_MS = 400;
  var TOLERANCE = 1.05;
  var bypassUntil = 0;

  function now() {
    return Date.now();
  }

  function withTimeout(promise, ms) {
    return Promise.race([
      promise,
      new Promise(function (_, reject) {
        setTimeout(function () { reject(new Error('guard-timeout')); }, ms);
      }),
    ]);
  }

  function delay(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  function readGuard() {
    var url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart?section_id=' + SECTION;
    return fetch(url, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('guard-http-' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-ml-cart-guard]');
        if (!node) throw new Error('guard-missing');
        return JSON.parse(node.textContent);
      });
  }

  function mismatches(data) {
    if (!data || !data.memberPricingExpected) return [];
    return (data.lines || []).filter(function (l) {
      if (!l.memberUnitCents || !l.quantity) return false;
      var expected = l.memberUnitCents * l.quantity;
      if (expected >= l.originalLineCents) return false; // nothing to discount
      if (!l.discounted) return true;
      return l.finalLineCents > Math.round(expected * TOLERANCE);
    });
  }

  function cartAgeMinutes(token) {
    try {
      var key = 'mlCartFirstSeen:' + token;
      var first = parseInt(localStorage.getItem(key), 10);
      if (!first) {
        localStorage.setItem(key, String(now()));
        return 0;
      }
      return Math.round((now() - first) / 60000);
    } catch (e) {
      return null;
    }
  }

  function track(data, bad, surface, repairAttempted, repairWorked) {
    try {
      if (!window.posthog || typeof window.posthog.capture !== 'function') return;
      window.posthog.capture('member_price_mismatch', {
        line_count: (data.lines || []).length,
        mismatched_line_count: bad.length,
        variant_ids: bad.map(function (l) { return l.variantId; }),
        handles: bad.map(function (l) { return l.handle; }),
        expected_line_cents: bad.map(function (l) { return l.memberUnitCents * l.quantity; }),
        actual_line_cents: bad.map(function (l) { return l.finalLineCents; }),
        undiscounted_count: bad.filter(function (l) { return !l.discounted; }).length,
        cart_age_minutes: cartAgeMinutes(data.token),
        membership_in_cart: !!data.membershipInCart,
        trial_in_cart: !!data.trialInCart,
        signed_in: !!data.signedIn,
        is_member: !!data.isMember,
        repair_attempted: repairAttempted,
        repair_worked: repairWorked,
        surface: surface,
      });
    } catch (e) {}
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (d) {
        if (!r.ok) throw d;
        return d;
      });
    });
  }

  // Remove and re-add each affected line, one at a time, keeping plan and properties.
  // Every removal is followed by its own re-add, so a failure never leaves a product out.
  function repair(bad) {
    var chain = Promise.resolve();
    bad.forEach(function (l) {
      chain = chain.then(function () {
        return postJson('/cart/change.js', { id: l.key, quantity: 0 })
          .then(function () {
            var item = { id: l.variantId, quantity: l.quantity };
            if (l.sellingPlanId) item.selling_plan = l.sellingPlanId;
            if (l.properties && typeof l.properties === 'object' && Object.keys(l.properties).length) item.properties = l.properties;
            return postJson('/cart/add.js', { items: [item] });
          })
          .then(function () { return delay(SETTLE_MS); });
      });
    });
    return chain.then(function () {
      document.dispatchEvent(new CustomEvent('monthlees:cart-updated'));
    });
  }

  function money(cents) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') return window.Shopify.formatMoney(cents);
    } catch (e) {}
    return '$' + (cents / 100).toFixed(2);
  }

  var CSS =
    '.ml-guard{max-width:min(520px,calc(100vw - 32px));padding:0;border:0;border-radius:16px;box-shadow:0 30px 80px -20px rgba(9,9,11,.45);color:#131316;font-family:var(--font-body--family,system-ui,sans-serif)}' +
    '.ml-guard::backdrop{background:rgba(9,9,11,.55)}' +
    '.ml-guard__card{display:flex;flex-direction:column;gap:12px;margin:0;padding:24px}' +
    '.ml-guard__title{margin:0;font-family:var(--font-heading--family,Georgia,serif);font-size:24px;font-weight:700;line-height:1.2}' +
    '.ml-guard__text{margin:0;font-size:15px;line-height:1.5;color:#3f3f46}' +
    '.ml-guard__list{margin:0;padding-left:18px;font-size:14px;line-height:1.5;color:#3f3f46}' +
    '.ml-guard__list b{color:#131316}' +
    '.ml-guard__actions{display:flex;flex-wrap:wrap;gap:8px;align-items:center}' +
    '.ml-guard__btn{min-height:48px;padding:0 20px;border:1px solid #131316;border-radius:999px;background:#fff;color:#131316;font:inherit;font-size:15px;font-weight:600;cursor:pointer}' +
    '.ml-guard__btn--primary{background:#18181b;color:#fff;border-color:#18181b}' +
    '.ml-guard__link{min-height:44px;padding:0 4px;border:0;background:none;color:#52525b;font:inherit;font-size:14px;text-decoration:underline;cursor:pointer}' +
    '.ml-guard__btn:focus-visible,.ml-guard__link:focus-visible{outline:2px solid #1d4ed8;outline-offset:2px}' +
    '.ml-guard__fine{margin:0;font-size:13px;line-height:1.5;color:#52525b}' +
    '.ml-guard__fine a{color:#131316;font-weight:600}';

  function injectCss() {
    if (document.getElementById('ml-guard-css')) return;
    var st = document.createElement('style');
    st.id = 'ml-guard-css';
    st.textContent = CSS;
    document.head.appendChild(st);
  }

  // A small, self-contained dialog: the guard must not depend on any cart UI's markup.
  function ask(bad, totalCents) {
    injectCss();
    return new Promise(function (resolve) {
      var d = document.createElement('dialog');
      d.className = 'ml-guard';
      d.setAttribute('aria-labelledby', 'ml-guard-title');
      var items = bad.map(function (l) {
        return '<li><b>' + escapeHtml(l.title) + '</b> · member price ' + money(l.memberUnitCents * l.quantity) + ', checkout would charge ' + money(l.finalLineCents) + '</li>';
      }).join('');
      d.innerHTML =
        '<form method="dialog" class="ml-guard__card">' +
        '<h2 id="ml-guard-title" class="ml-guard__title">Your member price didn’t reach checkout</h2>' +
        '<p class="ml-guard__text">We tried to fix it and couldn’t. So you’re not charged more than we showed you, checkout is paused.</p>' +
        '<ul class="ml-guard__list">' + items + '</ul>' +
        '<div class="ml-guard__actions">' +
        '<button class="ml-guard__btn ml-guard__btn--primary" value="retry">Try again</button>' +
        '<button class="ml-guard__btn" value="cart">Review my cart</button>' +
        '<button class="ml-guard__link" value="continue">Continue anyway · checkout total ' + money(totalCents) + '</button>' +
        '</div>' +
        '<p class="ml-guard__fine">Still stuck? <a href="/pages/contact">Contact us</a> and we’ll put the order through at your member price.</p>' +
        '</form>';
      document.body.appendChild(d);
      d.addEventListener('close', function () {
        var v = d.returnValue || 'cancel';
        d.remove();
        resolve(v);
      });
      if (typeof d.showModal === 'function') d.showModal();
      else resolve('continue');
    });
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Resolves true when checkout may proceed.
  function check(surface) {
    var data;
    // One membership line at quantity 1 before anything else (assets/ml-membership-qty-guard.js).
    var qty = window.MlMembershipQty ? withTimeout(window.MlMembershipQty.normalize(), TIMEOUT_MS).catch(function () {}) : Promise.resolve();
    return qty.then(function () { return withTimeout(readGuard(), TIMEOUT_MS); })
      .then(function (d) {
        data = d;
        var bad = mismatches(data);
        if (!bad.length) return true;
        return repair(bad)
          .then(function () { return withTimeout(readGuard(), TIMEOUT_MS); })
          .then(function (after) {
            var still = mismatches(after);
            track(data, bad, surface, true, still.length === 0);
            if (!still.length) return true;
            return ask(still, after.totalCents).then(function (choice) {
              if (choice === 'retry') return check(surface);
              if (choice === 'cart') {
                window.location.assign('/cart');
                return false;
              }
              if (choice === 'continue') {
                try { window.posthog && window.posthog.capture('member_price_mismatch_continued', { variant_ids: still.map(function (l) { return l.variantId; }), surface: surface }); } catch (e) {}
                return true;
              }
              return false;
            });
          }, function (err) {
            // The repair itself failed; report it and let checkout proceed rather than strand the buyer.
            track(data, bad, surface, true, false);
            return true;
          });
      })
      .catch(function () {
        return true; // fail open: no answer, no block
      });
  }

  function surfaceOf(el) {
    if (!el || !el.closest) return 'unknown';
    if (el.closest('[data-cart-drawer], .monthlees-cart-drawer, [data-ml-cart-drawer]')) return 'drawer';
    if (el.closest('[data-ml-cart], .monthlees-cart, form[action*="/cart"]')) return 'cart';
    return 'other';
  }

  function isCheckoutSubmit(form, submitter) {
    if (!form || !form.getAttribute) return false;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/checkout') !== -1) return true;
    if (submitter && submitter.name === 'checkout') return true;
    return false;
  }

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      var submitter = e.submitter || null;
      if (!isCheckoutSubmit(form, submitter)) return;
      if (now() < bypassUntil) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (submitter) submitter.setAttribute('aria-busy', 'true');
      check(surfaceOf(form)).then(function (ok) {
        if (submitter) submitter.removeAttribute('aria-busy');
        if (!ok) return;
        bypassUntil = now() + 3000;
        if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
        else window.location.assign('/checkout');
      });
    },
    true
  );

  document.addEventListener(
    'click',
    function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      var href = a.getAttribute('href') || '';
      if (!/^\/checkout(\/|\?|$)|\/checkouts?\//.test(href) && href.indexOf('/checkout') !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      if (now() < bypassUntil) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      check(surfaceOf(a)).then(function (ok) {
        if (!ok) return;
        bypassUntil = now() + 3000;
        window.location.assign(href);
      });
    },
    true
  );

  window.MlCheckoutGuard = { check: check, mismatches: mismatches };
})();
